"use client";

import {
  getEntityInstanceById,
  getEntityInstanceByIdNew,
} from "@/services/entity-instance.service";
import { useQuery } from "@tanstack/react-query";
import { useEntitySchemaByName } from "./schema/use-entity-schema-by-name";
import { useFieldNameStore } from "@/stores/entity-instance-field-store";
import { useMemo } from "react";

export const useEntityInstanceByIdWithoutRelations = ({
  entityName,
  id,
  placeholderInstance,
  fieldsToFetch,
  fetchAllFields = true,
  fetchDeepRelations = false,
}: {
  entityName: string;
  id: string;
  placeholderInstance?: unknown;
  fieldsToFetch?: string[];
  fetchAllFields?: boolean;
  fetchDeepRelations?: boolean;
}) => {
  const { entitySchemaWithRelations } = useEntitySchemaByName(entityName);

  const fieldNameStore = useFieldNameStore();

  const fieldsToFetchForRelationFieldNames = useMemo(() => {
    const fieldNameMap: Array<{
      fieldName: string;
      fieldsToFetch: Array<string>;
    }> = [];

    if (fetchAllFields) {
      entitySchemaWithRelations?.fields.forEach((field) => {
        if (field.type?.endsWith("ListData") && !fetchDeepRelations) {
          fieldNameMap.push({
            fieldName: field.name,
            fieldsToFetch: ["id"],
          });
        } else if (field.kind === "OBJECT") {
          fieldNameMap.push({
            fieldName: field.name,
            fieldsToFetch: field.fields?.map((field) => field.name) ?? [],
          });
        }
      });
    } else {
      const relationFields = entitySchemaWithRelations?.fields
        .filter((field) => field.kind === "OBJECT")
        .filter((field) => field.type?.endsWith("Data") && !field.type.endsWith("ListData"))
        .map((field) => {
          return (
            field.name.replace("Data", "").charAt(0).toUpperCase() +
            field.name.replace("Data", "").slice(1)
          );
        });
      relationFields?.forEach((field) => {
        const fieldsToFetchForThat = fieldNameStore.eilStates[field]?.field;
        if (fieldsToFetchForThat) {
          fieldNameMap.push({
            fieldName: field,
            fieldsToFetch: [fieldsToFetchForThat],
          });
        }
      });
    }

    return fieldNameMap;
  }, [
    fetchAllFields,
    entitySchemaWithRelations?.fields,
    fetchDeepRelations,
    fieldNameStore.eilStates,
  ]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "entity-instance-by-id",
      entityName,
      id,
      fieldsToFetch?.sort(),
      fieldsToFetchForRelationFieldNames.flatMap((x) => x.fieldsToFetch),
    ],
    queryFn: async () => {
      return await getEntityInstanceById({
        entityName: entityName,
        id,
        entitySchema: entitySchemaWithRelations,
        fieldsToFetch,
        fieldsToFetchForRelationFieldNames,
      });
    },
    enabled: !!entitySchemaWithRelations && !!id,
    placeholderData: {
      entityInstance: placeholderInstance as object[],
    },
  });

  return {
    entityInstance: data,
    isLoadingEntityInstance: isLoading,
    isFetchingEntityInstance: isFetching,
  };
};

export const useEntityInstanceById = ({
  entityName,
  id,
  placeholderInstance,
  fieldsToFetch,
}: {
  entityName: string;
  id: string;
  placeholderInstance?: object;
  fieldsToFetch?: string[];
}) => {
  const query = useQuery({
    queryKey: ["entity-instance-by-id", entityName, id, fieldsToFetch?.sort()],
    queryFn: async () => {
      return await getEntityInstanceByIdNew({
        entityName: entityName,
        id: id,
        fieldsToFetch: fieldsToFetch,
      });
    },
    enabled: !!id,
    placeholderData: {
      data: (placeholderInstance ?? ({} as object)) as object,
      entityName: entityName,
      id: id,
    },
  });

  return query;
};
